import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ErrorService} from '@services/error/error.service';
import {Observable, Subject, throwError} from 'rxjs';
import {IResponse} from '@interfaces/response';
import {IFileUpload, IMediaItem, IMediaProofResult} from '@interfaces/media';
import {createHttpParams} from '@shared/functions';
import {catchError, share} from 'rxjs/operators';
import {IWidgetApplication} from '@interfaces/widget-application';
import {IPayload} from '@interfaces/payload';
import {IFolder, IFolderContent} from '@interfaces/folder';
import {MediaSortType} from '../../types/media.type';
import {MediaTypeEnum} from '@shared/enums/media-type.enum';

@Injectable({
    providedIn: 'root'
})

export class MediaService {

    constructor(
        private http: HttpClient,
        private errorService: ErrorService
    ) {
    }

    // using for tabs in addMedia modal, for prevent circularities
    public backToAddMediaModal$ = new Subject();

    public get backAddMediaModalSub$(): Observable<any> {
        return this.backToAddMediaModal$.asObservable();
    }

    public getAllMedia({limit, skip = 0, order = '-created_at', search = '', media_type = '', proofEnabled}:
                           { limit: number, skip?: number, order?: string, search?: string, media_type?: string, proofEnabled?: boolean }
    ): Observable<IResponse<IMediaItem>> {

        let params = createHttpParams({
            skip,
            limit,
            order,
            media_type,
            filter: search
        });

        if (proofEnabled !== undefined) {
            params = params.set('proofEnabled', String(proofEnabled));
        }

        return this.http
            .get<IResponse<IMediaItem>>('filesystem/medias', {params})
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public fetchMediaById(id: number): Observable<IMediaItem> {
        return this.http.get<IMediaItem>(`filesystem/medias/${id}`);
    }

    public setProofEnabled(id: number, proofEnabled: boolean): Observable<IMediaItem> {
        return this.http.patch<IMediaItem>(`filesystem/medias/${id}`, {proofEnabled})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err, false);
                    return throwError(err);
                }));
    }

    public setMediasProofEnabled(ids: number[], proofEnabled: boolean): Observable<IMediaProofResult> {
        return this.http.post<IMediaProofResult>('filesystem/medias/proof', {ids, proofEnabled})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err, false);
                    return throwError(err);
                }));
    }

    public editMedia(media: IMediaItem): Observable<IMediaItem> {
        return this.http.patch<IMediaItem>(`filesystem/medias/${media.id}`,
            {name: media.name, description: media.description})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                }));
    }

    public addMedia(data: any, folderId: number) {
        const params = {
            ...data,
            size: null,
            duration: null,
        };
        const addMediaRequest = this.http.post(`filesystem/folders/${folderId}/medias`, params)
            .pipe(share());

        addMediaRequest
            .subscribe({
                error: error => this.errorService.showError(error)
            });

        return addMediaRequest;
    }

    public uploadFileUrl(file: IFileUpload, folderId: number, item?: any): Observable<any> {
        const stream = this.http.post(`filesystem/folders/${folderId}/medias`, {...file, duration: item.duration})
            .pipe(share());

        stream.subscribe(
            () => {
            },
            ({error}) => this.errorService.showError(error));
        return stream;
    }

    // TODO 15.06.2021 oleksandrliashko: check code below
    // TODO it was copied from old media service
    // TODO 12.08.22 IvanKosheliev: CODE ABOVE NEED TO BE REFACTORED


    public uploadFile(file: IFileUpload): Observable<IFileUpload> {
        return this.http.post<IFileUpload>(`medias`, {...file})
            .pipe(
                share(),
                catchError((err) => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public removeMedias(medias: number[]): Observable<any> {
        return this.http.request('delete', 'medias', {body: {media: medias}})
            .pipe(
                catchError((err) => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public fetchWidgetApps(): Observable<IWidgetApplication[]> {
        return this.http.get<IWidgetApplication[]>('widget-apps')
            .pipe(
                catchError((err) => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            )
    }

    public createMediaFolder({name, parentId}: { name: string, parentId: number }): Observable<IFolder> {
        return this.http.post<IFolder>('filesystem/folders', {name, parentId}).pipe(
            catchError(err => {
                this.errorService.showError(err);
                return throwError(err);
            })
        )
    }

    public updateMediaFolderData(name: string, folderId: number): Observable<IFolder> {
        return this.http.patch<IFolder>(`filesystem/folders/${folderId}`, {name}).pipe(
            catchError(err => {
                this.errorService.showError(err);
                return throwError(err)
            })
        )
    }

    public copyMediasContent({
                                 sourceFolderId,
                                 targetFolderId,
                                 mediaIds
                             }: { sourceFolderId: number, targetFolderId: number, mediaIds: number[] }): Observable<string> {
        return this.http.post<string>('filesystem/copy', {sourceFolderId, targetFolderId, mediaIds})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err)
                })
            )
    }

    public moveMediasContent({folderIds, mediaIds, destinationId}: {
        folderIds: number[],
        mediaIds: number[],
        destinationId: number
    }): Observable<any> {
        return this.http.post<any>('filesystem/move', {folderIds, mediaIds, destinationId})
            .pipe(
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err)
                })
            )
    }

    public fetchFolderContent(folderId: number, payload: IPayload<MediaSortType>,
                              mediaTypes: MediaTypeEnum[] | '', search: string,
                              onlyMedias: boolean, onlyFolders: boolean): Observable<IFolderContent<IMediaItem>> {
        let params = createHttpParams({
            skip: payload.skip,
            limit: payload.limit,
            order: payload.order,
            sort: payload.sort,
            search: search,
            onlyMedias: String(onlyMedias),
            onlyFolders: String(onlyFolders)
        });

        if (Array.isArray(mediaTypes)) {
            mediaTypes.forEach((param) => {
                params = params.append('mediaTypes', param)
            })
        }

        return this.http.get<IFolderContent<IMediaItem>>(`filesystem/folders/${folderId}/content`, {params})
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            );
    }

    public deleteMediaFolders(ids: number[]): Observable<string> {
        return this.http.request<string>('delete', 'filesystem/folders/', {body: {ids}})
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            )
    }

    public deleteMediaFromFolder(folderId: number, mediaIds: number[]): Observable<string> {
        return this.http.request<string>('delete', `filesystem/folders/${folderId}/medias`, {body: {ids:mediaIds}})
            .pipe(
                share(),
                catchError(err => {
                    this.errorService.showError(err);
                    return throwError(err);
                })
            )
    }
}
